import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Card, CardContent } from '~/components/ui/card'
import { Badge } from '~/components/ui/badge'
import { Markdown } from '~/components/ui/markdown'
import { cn, shortId } from '~/lib/utils'
import type { Citation } from '~/api/types'

export interface CitationCardProps {
    citation: Citation
    /** 목록 내 순번(선택). */
    index?: number
}

/**
 * 근거 출처 카드. 문서·조항·페이지·스니펫을 노출한다.
 * HACCP 규정집 근거 제시로 신뢰를 주는 핵심 컴포넌트이다.
 */
export function CitationCard({ citation, index }: CitationCardProps) {
    const { title, documentId, clause, page, snippet, score } = citation
    const [open, setOpen] = useState(false)
    // 스니펫이 길면 접고, "전문 보기"로 펼친다.
    const long = (snippet?.length ?? 0) > 180

    return (
        <Card>
            <CardContent className='space-y-2 p-3'>
                <div className='flex items-center gap-2'>
                    {typeof index === 'number' ? (
                        <Badge tone='info'>근거 {index + 1}</Badge>
                    ) : (
                        <Badge tone='info'>근거</Badge>
                    )}
                    <span className='text-foreground min-w-0 flex-1 truncate text-sm font-medium'>
                        {title ??
                            (documentId
                                ? `문서 ${shortId(documentId)}`
                                : '문서')}
                    </span>
                </div>

                <div className='text-muted-foreground flex flex-wrap gap-2 text-xs'>
                    {clause ? <span>조항 {clause}</span> : null}
                    {typeof page === 'number' ? <span>p.{page}</span> : null}
                    {typeof score === 'number' ? (
                        <span>유사도 {score.toFixed(2)}</span>
                    ) : null}
                </div>

                {snippet ? (
                    <div className='border-border border-l-2 pl-2'>
                        <div
                            className={cn(
                                'text-foreground [&_*]:break-words',
                                long && !open && 'line-clamp-4'
                            )}>
                            <Markdown>{snippet}</Markdown>
                        </div>
                        {long ? (
                            <button
                                type='button'
                                onClick={() => setOpen((o) => !o)}
                                className='text-primary mt-1 inline-flex items-center gap-0.5 text-xs hover:underline'>
                                {open ? (
                                    <>
                                        접기 <ChevronUp className='size-3' />
                                    </>
                                ) : (
                                    <>
                                        전문 보기{' '}
                                        <ChevronDown className='size-3' />
                                    </>
                                )}
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    )
}
